import { useTranslation } from 'react-i18next'
import { Button } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Icon from 'components/priority/Icon/Icon'
import Text from 'components/priority/Text/Text'
import type { EnhanceController } from '@/hooks/caseImages'
import { useUpscalePriceLabel } from '@/hooks/caseImages/useUpscalePriceLabel'
import { resolveSourceImageUrl } from '@/utils/caseImages/resolveCaseImageDisplayUrl'
import { variantErrorLocaleKey } from '@/utils/caseImages/variantErrorCodes'
import { CASE_IMAGE_JOB_STATUS_KEY } from '@messenger/consts'
import { isAcceptedUpscaleVariant } from '@messenger/utils'
import { CompareSlider } from '@messenger/ui/components/compare-slider'
import { EnhanceGenerativeNotice } from './EnhanceGenerativeNotice'
import * as S from './EnhancePanel.styled'
import { CASE_IMAGES_TEST_IDS } from './caseImagesTestIds'

type EnhancePanelProps = {
  enhance: EnhanceController
  /**
   * Where the panel is drawn. `takeover` replaces the upload dropzone inside
   * the case details modal; `modal` is the stacked, centred layout inside the
   * editor's Enhance modal (`EnhanceModal`). Defaults to `takeover`.
   */
  layout?: S.EnhanceLayout
}

type ThumbnailOptions = {
  /** Ease the photo between 50% and 100% opacity while a generation runs. */
  pulsing?: boolean
  /** Show the sparkle badge on the corner (the modal's disclosure only). */
  badge?: boolean
}

/**
 * The Enhance UI for one image. It shows whichever stage `useEnhanceImage`
 * is in: disclosure, enhancing, complete, error, full-size compare of an
 * accepted enhancement, the revert confirmation, or the notice that the
 * photo changed on the server while the screen was open. The same component
 * serves both hosts; the `layout` prop only changes how the pieces sit
 * (side by side in the case details takeover, stacked in the editor's
 * modal). The price comes from the pricing atom; until it loads, copy is
 * shown without an amount.
 */
export const EnhancePanel = ({
  enhance,
  layout = 'takeover',
}: EnhancePanelProps) => {
  const { t } = useTranslation()
  const price = useUpscalePriceLabel()
  const { state } = enhance
  if (state.stage === 'idle') return null

  const modal = layout === 'modal'
  const sourceUrl = resolveSourceImageUrl(state.tile.image)
  const sliderLabel = t('caseImages.enhance.sliderLabel')
  // The Figma modal draws its second button in the light, bordered style;
  // the takeover keeps the coloured secondary it has always used.
  const secondaryVariant = modal ? { default: true } : { secondary: true }
  const titleSize = modal ? '16px' : '18px'
  const centred = modal ? 'center' : undefined

  const thumbnail = ({ pulsing, badge }: ThumbnailOptions = {}) => (
    <S.ThumbnailAnchor $layout={layout}>
      <S.PanelThumbnail
        src={sourceUrl}
        alt=""
        $layout={layout}
        $pulsing={pulsing}
      />
      {badge && (
        <S.SparkleBadge aria-hidden="true">
          <Icon icon="sparkles" fill="gray1" width="24px" height="24px" />
        </S.SparkleBadge>
      )}
    </S.ThumbnailAnchor>
  )

  if (state.stage === 'disclosure') {
    return (
      <S.Panel
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}
      >
        {thumbnail({ badge: modal })}
        <S.PanelCopy
          $layout={layout}
          $priced
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_DISCLOSURE}
        >
          <Text size={titleSize} weight="600" line="20px">
            {t('caseImages.enhance.disclosureTitle')}
          </Text>
          <Text
            size={modal ? '16px' : '14px'}
            weight={modal ? '600' : '500'}
            color={modal ? undefined : 'gray2'}
            line="20px"
            whiteSpace="nowrap"
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PRICE}
          >
            {price
              ? t('caseImages.enhance.pricePerUse', { price })
              : t('caseImages.enhance.pricePerUseNoAmount')}
          </Text>
          <Text
            size="14px"
            weight={modal ? '500' : undefined}
            color={modal ? 'gray2' : undefined}
            line={modal ? '16px' : '1.5'}
          >
            {t('caseImages.enhance.disclosureBody')}
          </Text>
          <EnhanceGenerativeNotice layout={layout} />
        </S.PanelCopy>
        <S.Actions $layout={layout}>
          <Button
            primary
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_DISCLOSURE_CONFIRM}
            onClick={enhance.confirmDisclosure}
          >
            {t('caseImages.enhance.enhanceButton')}
          </Button>
          <Button
            {...secondaryVariant}
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_DISCLOSURE_CANCEL}
            onClick={enhance.cancel}
          >
            {t('caseImages.enhance.cancelButton')}
          </Button>
        </S.Actions>
      </S.Panel>
    )
  }

  if (state.stage === 'enhancing') {
    const queued = state.status === CASE_IMAGE_JOB_STATUS_KEY.QUEUED
    return (
      <S.Panel
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}
      >
        {thumbnail({ pulsing: true })}
        <S.PanelCopy
          $layout={layout}
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_ENHANCING}
        >
          <Text
            size={titleSize}
            weight="600"
            align={centred}
            role="status"
            aria-live="polite"
          >
            {t('caseImages.enhance.enhancingTitle')}
            {queued ? '…' : ''}
          </Text>
          {modal ? (
            <S.ProgressBar
              role="progressbar"
              aria-label={t('caseImages.enhance.enhancingTitle')}
              data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PROGRESS}
            >
              <S.ProgressTrack />
            </S.ProgressBar>
          ) : (
            <Text size="14px" line="1.5" color="gray2">
              {t('caseImages.enhance.enhancingBody')}
            </Text>
          )}
        </S.PanelCopy>
        <S.Actions $layout={layout}>
          <Button
            {...secondaryVariant}
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_ENHANCING_CANCEL}
            onClick={enhance.cancel}
          >
            {t('caseImages.enhance.cancelButton')}
          </Button>
        </S.Actions>
      </S.Panel>
    )
  }

  if (state.stage === 'complete') {
    return (
      <S.StackPanel
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}
      >
        <Flex
          column
          gap="4px"
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_COMPLETE}
        >
          <Text size={titleSize} weight="600" align={centred}>
            {t('caseImages.enhance.completeTitle')}
          </Text>
          <Text size="14px" color="gray2" align={centred}>
            {t('caseImages.enhance.completeBody')}
          </Text>
          {/* The disclosure is skipped once dismissed, so the notice is
              repeated here, where every run ends before the accept. */}
          <EnhanceGenerativeNotice layout={layout} centred={modal} />
        </Flex>
        <CompareSlider
          data-testid={CASE_IMAGES_TEST_IDS.COMPARE_SLIDER}
          beforeSrc={sourceUrl}
          afterSrc={state.url}
          beforeLabel={t('caseImages.enhance.original')}
          afterLabel={t('caseImages.enhance.enhanced')}
          handleLabel={sliderLabel}
        />
        <S.Actions $layout={layout}>
          <Button
            primary
            disabled={state.accepting}
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_ACCEPT}
            onClick={() => void enhance.accept()}
          >
            {state.accepting
              ? t('caseImages.enhance.accepting')
              : price
                ? t('caseImages.enhance.acceptButton', { price })
                : t('caseImages.enhance.acceptButtonNoAmount')}
          </Button>
          <Button
            {...secondaryVariant}
            disabled={state.accepting}
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_COMPLETE_CANCEL}
            onClick={enhance.cancel}
          >
            {t('caseImages.enhance.discardButton')}
          </Button>
        </S.Actions>
      </S.StackPanel>
    )
  }

  if (state.stage === 'error') {
    return (
      <S.Panel
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}
      >
        {/* The compact error dialog in the editor has no thumbnail. */}
        {!modal && thumbnail()}
        <S.PanelCopy
          $layout={layout}
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_ERROR}
        >
          <Text
            size="16px"
            weight="600"
            line="20px"
            role="alert"
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_ERROR_MESSAGE}
            data-error-code={state.code ?? undefined}
          >
            {t(variantErrorLocaleKey(state.code), {
              defaultValue: t('caseImages.enhance.errorTitle'),
            })}
          </Text>
        </S.PanelCopy>
        <S.Actions $layout={layout} $spread={modal}>
          {state.retryable && (
            <Button
              primary
              data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_TRY_AGAIN}
              onClick={enhance.tryAgain}
            >
              {t('caseImages.enhance.tryAgainButton')}
            </Button>
          )}
          <Button
            {...secondaryVariant}
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_ERROR_CANCEL}
            onClick={enhance.cancel}
          >
            {t('caseImages.enhance.cancelButton')}
          </Button>
        </S.Actions>
      </S.Panel>
    )
  }

  if (state.stage === 'compare') {
    // Only accepted upscales belong here; background-removal cutouts are a
    // different feature and must not show as "previous enhancements".
    const previous = state.tile.variants.filter(isAcceptedUpscaleVariant)
    return (
      <S.StackPanel
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}
      >
        <Flex
          column
          gap="4px"
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_COMPARE}
        >
          <Text size={titleSize} weight="600" align={centred}>
            {t('caseImages.enhance.compareTitle')}
          </Text>
          <Text size="14px" color="gray2" align={centred}>
            {t('caseImages.enhance.compareBody')}
          </Text>
        </Flex>
        <CompareSlider
          key={state.variant.id}
          data-testid={CASE_IMAGES_TEST_IDS.COMPARE_SLIDER}
          beforeSrc={sourceUrl}
          afterSrc={state.variant.url as string}
          beforeLabel={t('caseImages.enhance.original')}
          afterLabel={t('caseImages.enhance.enhanced')}
          handleLabel={sliderLabel}
        />
        {previous.length > 1 && (
          <Flex column gap="8px" align={centred}>
            <Text size="12px" weight="600" color="gray2">
              {t('caseImages.enhance.previousEnhancements')}
            </Text>
            <S.PreviousRow data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PREVIOUS}>
              {previous.map((variant) => (
                <S.PreviousThumb
                  key={variant.id}
                  type="button"
                  $selected={variant.id === state.variant.id}
                  aria-pressed={variant.id === state.variant.id}
                  onClick={() => enhance.openCompare(state.tile, variant)}
                >
                  <img src={variant.url as string} alt="" />
                </S.PreviousThumb>
              ))}
            </S.PreviousRow>
          </Flex>
        )}
        <S.Actions $layout={layout}>
          <Button
            primary
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_COMPARE_DONE}
            onClick={enhance.cancel}
          >
            {t('caseImages.enhance.doneButton')}
          </Button>
          {state.tile.display.shownVariant && (
            <Button
              {...secondaryVariant}
              data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_COMPARE_REVERT}
              onClick={() => enhance.openRevert(state.tile)}
            >
              {t('caseImages.tile.revert')}
            </Button>
          )}
        </S.Actions>
      </S.StackPanel>
    )
  }

  if (state.stage === 'changed') {
    return (
      <S.Panel
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}
      >
        {!modal && thumbnail()}
        <S.PanelCopy
          $layout={layout}
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_CHANGED}
        >
          <Text
            size={titleSize}
            weight="600"
            line="20px"
            align={centred}
            role="alert"
          >
            {t('caseImages.enhance.changedTitle')}
          </Text>
          <Text size="14px" line="1.5" align={centred}>
            {t('caseImages.enhance.changedBody')}
          </Text>
        </S.PanelCopy>
        <S.Actions $layout={layout}>
          <Button
            primary
            data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_CHANGED_DONE}
            onClick={enhance.cancel}
          >
            {t('caseImages.enhance.doneButton')}
          </Button>
        </S.Actions>
      </S.Panel>
    )
  }

  // state.stage === 'revert'
  return (
    <S.Panel $layout={layout} data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_PANEL}>
      {thumbnail()}
      <S.PanelCopy
        $layout={layout}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_REVERT_CONFIRM}
      >
        <Text size={titleSize} weight="600" align={centred}>
          {t('caseImages.enhance.revertTitle')}
        </Text>
        <Text size="14px" line="1.5" align={centred}>
          {t('caseImages.enhance.revertBody')}
        </Text>
      </S.PanelCopy>
      <S.Actions $layout={layout}>
        <Button
          primary
          disabled={state.saving}
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_REVERT_ACCEPT}
          onClick={() => void enhance.confirmRevert()}
        >
          {t('caseImages.enhance.revertButton')}
        </Button>
        <Button
          {...secondaryVariant}
          disabled={state.saving}
          data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_REVERT_KEEP}
          onClick={enhance.cancel}
        >
          {t('caseImages.enhance.keepButton')}
        </Button>
      </S.Actions>
    </S.Panel>
  )
}
